/**
 * VerifyEmailPage — handles the /verify-email?token=... link from email.
 *
 * Uses POST (not GET) to consume the token so email-scanner bots that
 * fetch links cannot accidentally verify the account before the user sees it.
 *
 * Flow:
 *   1. User clicks link in email → lands here
 *   2. useEffect fires → POST /auth/verify-email with token
 *   3a. Success → checkmark + "Fazer login" button
 *   3b. Token expired → error + note to use dashboard resend button
 *   3c. Already verified → treated as success
 */
import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API_URL } from "@/utils/apiConfig";

const API = API_URL || "";

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [status, setStatus] = useState("loading"); // "loading" | "success" | "expired" | "error"
  const [message, setMessage] = useState("");
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return; // prevent double-fire in StrictMode
    calledRef.current = true;
    if (!token) { setStatus("error"); setMessage("Link inválido — token em falta."); return; }
    axios.post(`${API}/api/v1/auth/verify-email`, { token })
      .then(() => setStatus("success"))
      .catch(err => {
        const detail = err.response?.data?.detail || "";
        if (detail.includes("expirado")) { setStatus("expired"); setMessage(detail); }
        else { setStatus("error"); setMessage(detail || "Erro ao verificar email."); }
      });
  }, [token]);

  return (
    <div style={{ minHeight: "100vh", background: "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Segoe UI', Arial, sans-serif", padding: "24px 16px" }}>
      <div style={{ maxWidth: 480, width: "100%", background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.10)" }}>
        {/* Header */}
        <div style={{ background: "#18181b", padding: "28px 32px", textAlign: "center" }}>
          <div style={{ display: "inline-block", width: 48, height: 48, borderRadius: "50%", border: "2px solid #CDA34F", background: "#000", lineHeight: "44px", textAlign: "center", fontSize: 18, fontWeight: 900, color: "#CDA34F" }}>KS</div>
          <h1 style={{ color: "#fff", margin: "10px 0 0", fontSize: 20, fontWeight: 700, letterSpacing: "1.5px" }}>
            KAISO <span style={{ color: "#CDA34F" }}>SYSTEM</span>
          </h1>
        </div>
        <div style={{ height: 4, background: status === "success" ? "#16a34a" : status === "loading" ? "#CDA34F" : "#ef4444" }} />

        {/* Body */}
        <div style={{ padding: "36px 32px", textAlign: "center" }}>
          {status === "loading" && (
            <>
              <div style={{ width: 56, height: 56, border: "4px solid #e4e4e7", borderTopColor: "#CDA34F", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 20px" }} />
              <p style={{ fontSize: 16, color: "#52525b" }}>A verificar o seu email...</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </>
          )}

          {status === "success" && (
            <>
              <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                <svg viewBox="0 0 24 24" style={{ width: 32, height: 32, color: "#16a34a" }} fill="none" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: "#18181b", margin: "0 0 8px" }}>Email verificado!</h2>
              <p style={{ fontSize: 14, color: "#52525b", lineHeight: 1.6, margin: "0 0 28px" }}>
                A sua conta está confirmada. Já pode fazer login e utilizar o Kaiso System normalmente.
              </p>
              <Link to="/login" style={{ display: "inline-block", background: "#18181b", color: "#fff", padding: "12px 32px", borderRadius: 8, textDecoration: "none", fontWeight: 700, fontSize: 15 }}>
                Fazer login
              </Link>
            </>
          )}

          {status === "expired" && (
            <>
              <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                <svg viewBox="0 0 24 24" style={{ width: 32, height: 32, color: "#d97706" }} fill="none" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="10" /><path strokeLinecap="round" d="M12 8v4m0 4h.01" /></svg>
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: "#18181b", margin: "0 0 8px" }}>Link expirado</h2>
              <p style={{ fontSize: 14, color: "#52525b", lineHeight: 1.6, margin: "0 0 28px" }}>
                Este link de verificação expirou (validade 48 horas).<br />
                Faça login no dashboard e clique em <strong>"Reenviar email de verificação"</strong> para obter um novo link.
              </p>
              <Link to="/login" style={{ display: "inline-block", background: "#18181b", color: "#fff", padding: "12px 32px", borderRadius: 8, textDecoration: "none", fontWeight: 700, fontSize: 15 }}>
                Ir para o dashboard
              </Link>
            </>
          )}

          {status === "error" && (
            <>
              <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                <svg viewBox="0 0 24 24" style={{ width: 32, height: 32, color: "#dc2626" }} fill="none" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="10" /><path strokeLinecap="round" d="M15 9l-6 6M9 9l6 6" /></svg>
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: "#18181b", margin: "0 0 8px" }}>Link inválido</h2>
              <p style={{ fontSize: 14, color: "#52525b", lineHeight: 1.6, margin: "0 0 8px" }}>{message}</p>
              <p style={{ fontSize: 13, color: "#71717a", margin: "0 0 28px" }}>
                Se o seu email já foi verificado anteriormente, pode ignorar esta mensagem.
              </p>
              <Link to="/login" style={{ display: "inline-block", background: "#18181b", color: "#fff", padding: "12px 32px", borderRadius: 8, textDecoration: "none", fontWeight: 700, fontSize: 15 }}>
                Ir para o login
              </Link>
            </>
          )}
        </div>

        <div style={{ background: "#fafafa", padding: "16px 32px", textAlign: "center", borderTop: "1px solid #e4e4e7" }}>
          <p style={{ fontSize: 11, color: "#a1a1aa", margin: 0 }}>Kaiso System — Gestão Inteligente de Restaurantes</p>
        </div>
      </div>
    </div>
  );
}
